import { useEffect, useState } from "react";
import { Building2, Plus, TrendingUp, UserCheck, AlertCircle } from "lucide-react";
import { DataTablePattern } from "../../patterns/DataTablePattern";
import { Button } from "../../primitives/Button";
import { getAmbassadorClients } from "../../../api/admin";
import { cn } from "../../utils/cn";

type AmbassadorClientsTabProps = {
  ambassadorId: string;
};

export function AmbassadorClientsTab({ ambassadorId }: AmbassadorClientsTabProps) {
  const [clients, setClients] = useState<any[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    atRisk: 0,
    newThisMonth: 0,
  });
  const [performance, setPerformance] = useState({
    creditsUsed: 0,
    submissionsGenerated: 0,
    salesAccepted: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getAmbassadorClients(ambassadorId)
      .then((data) => {
        if (!mounted) return;
        setClients(data.clients || []);
        setStats(data.stats || stats);
        setPerformance(data.performance || performance);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load clients:", error);
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [ambassadorId]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "Actif";
      case "lead":
        return "Prospect";
      case "on_hold":
        return "En attente";
      case "churned":
        return "Perdu";
      default:
        return status;
    }
  };

  const getStatusTone = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "lead":
        return "warning";
      case "on_hold":
        return "warning";
      case "churned":
        return "danger";
      default:
        return "neutral";
    }
  };

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="text-sm text-muted">Chargement des clients...</div>
      </div>
    );
  }

  const rows = clients.map((client) => ({
    id: client.organization.id,
    name: client.organization.name,
    status: client.profile.lifecycle_status,
    score: client.score || "—",
    region: client.organization.province || "—",
    activatedAt: client.activated_at
      ? new Date(client.activated_at).toLocaleDateString("fr-CA")
      : "—",
    xlHubActive: client.xlhub_active ? "Oui" : "Non",
    volume: client.volume ? `$${client.volume.toLocaleString()}` : "—",
  }));

  return (
    <div className="space-y-6">
      {/* Indicateurs */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-md border border-border bg-surface p-4">
          <div className="text-xs text-muted mb-1">Total clients</div>
          <div className="text-2xl font-semibold text-text">{stats.total}</div>
        </div>
        <div className="rounded-md border border-border bg-surface p-4">
          <div className="text-xs text-muted mb-1">Clients actifs</div>
          <div className="text-2xl font-semibold text-success">{stats.active}</div>
        </div>
        <div className="rounded-md border border-border bg-surface p-4">
          <div className="text-xs text-muted mb-1">À risque</div>
          <div className="text-2xl font-semibold text-warning">{stats.atRisk}</div>
        </div>
        <div className="rounded-md border border-border bg-surface p-4">
          <div className="text-xs text-muted mb-1">Nouveaux ce mois</div>
          <div className="text-2xl font-semibold text-info">{stats.newThisMonth}</div>
        </div>
      </div>

      {/* Performance */}
      <div className="rounded-md border border-border bg-surface p-6">
        <h3 className="text-sm font-semibold text-text mb-4 flex items-center gap-2">
          <TrendingUp size={16} strokeWidth={1.5} />
          Performance portefeuille
        </h3>
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <div className="text-xs text-muted mb-1">Volume crédits utilisés</div>
            <div className="text-lg font-semibold text-text">{performance.creditsUsed.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Soumissions générées</div>
            <div className="text-lg font-semibold text-text">{performance.submissionsGenerated}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Ventes acceptées</div>
            <div className="text-lg font-semibold text-text">{performance.salesAccepted}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Taux conversion</div>
            <div className="text-lg font-semibold text-text">
              {performance.conversionRate > 0 ? `${performance.conversionRate.toFixed(1)}%` : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Liste des fermes */}
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text">Liste des fermes liées</h3>
          <Button
            label="+ Associer nouvelle ferme"
            intent="primary"
            size="sm"
            icon={<Plus size={14} strokeWidth={1.5} />}
            onClick={() => alert("À implémenter : Modal pour associer une ferme")}
          />
        </div>

        <DataTablePattern
          title=""
          columns={[
            { key: "name", label: "Nom ferme", type: "text" },
            { key: "status", label: "Statut lifecycle", type: "custom" },
            { key: "score", label: "Score", type: "text" },
            { key: "region", label: "Région", type: "text" },
            { key: "activatedAt", label: "Date activation", type: "text" },
            { key: "xlHubActive", label: "XLHub actif", type: "text" },
            { key: "volume", label: "Volume", type: "text" },
          ]}
          rows={rows.map((row) => ({
            ...row,
            status: (
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                  getStatusTone(row.status) === "success"
                    ? "bg-success/10 text-success"
                    : getStatusTone(row.status) === "warning"
                    ? "bg-warning/10 text-warning"
                    : getStatusTone(row.status) === "danger"
                    ? "bg-danger/10 text-danger"
                    : "bg-muted/10 text-muted"
                )}
              >
                {getStatusLabel(row.status)}
              </span>
            ),
          }))}
          rowActions={[
            {
              label: "Voir cockpit",
              icon: <Building2 size={14} strokeWidth={1.5} />,
              onClick: (row) => {
                window.location.hash = `#/clients?selected=${row.id}`;
              },
            },
            {
              label: "Retirer",
              icon: <Plus size={14} strokeWidth={1.5} className="rotate-45" />,
              onClick: (row) => {
                if (confirm("Êtes-vous sûr de vouloir retirer cette ferme du portefeuille ?")) {
                  alert("À implémenter");
                }
              },
            },
          ]}
        />
      </div>
    </div>
  );
}
